/**
 * Header Component
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@hooks/useAuth';
import './Header.scss';

const LANGS = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'ta', name: 'தமிழ்', flag: '🇮🇳' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'gu', name: 'ગુજરાતી', flag: '🇮🇳' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

const LangSelector = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const cur = (i18n.language || 'en').split('-')[0];
  const flag = LANGS.find(l => l.code === cur)?.flag || '🌐';

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={ref} className="lang-selector">
      <button onClick={() => setOpen(!open)} className="lang-selector__btn">
        {flag} {cur.toUpperCase()} ▾
      </button>
      {open && (
        <div className="lang-selector__dropdown">
          {LANGS.map(l => (
            <button
              key={l.code}
              onClick={() => { i18n.changeLanguage(l.code); setOpen(false); }}
              className={`lang-selector__option ${l.code === cur ? 'lang-selector__option--active' : ''}`}
            >
              {l.flag} {l.name} {l.code === cur && '✓'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const links = [
    { to: '/vendors', label: t('nav.vendors') },
    { to: '/venues', label: t('nav.venues') },
    { to: '/planning-tools', label: t('nav.planningTools') },
  ];

  return (
    <header className="header">
      <div className="header__container">
        <Link to="/" className="header__logo">
          <img src="/logo-128.png" alt="MyWeddingWiz.com" style={{ height: 48, marginRight: 8, verticalAlign: 'middle' }} />
          <span className="header__logo-text"><span className="header__logo-my">My</span><span className="header__logo-wedding">Wedding</span><span className="header__logo-wiz">Wiz</span><span className="header__logo-dot">.com</span></span>
        </Link>

        <nav className="header__nav">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => `header__link ${isActive ? 'active' : ''}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="header__actions">
          <LangSelector />

          {isAuthenticated ? (
            <>
              <Link to={
                user?.role === 'vendor' ? '/vendor' :
                user?.role === 'admin' || user?.role === 'super_admin' ? '/admin' :
                user?.role === 'venue_owner' ? '/venue' :
                user?.role === 'coordinator' ? '/coordinator' :
                '/dashboard'
              } className="btn btn-ghost btn-sm">
                {user?.role === 'admin' || user?.role === 'super_admin' ? 'Admin Panel' : t('nav.dashboard')}
              </Link>
              <button onClick={handleLogout} className="btn btn-secondary btn-sm">{t('nav.logout')}</button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-ghost btn-sm">{t('nav.login')}</Link>
              <Link to="/register" className="btn btn-primary btn-sm">{t('nav.register')}</Link>
            </>
          )}

          <button className="header__menu-btn" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            <i className={`bi bi-${menuOpen ? 'x' : 'list'}`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="header__mobile-menu">
          <nav className="header__mobile-nav">
            {links.map(l => (
              <NavLink key={l.to} to={l.to} className={({ isActive }) => `header__mobile-link ${isActive ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>
                {l.label}
              </NavLink>
            ))}
            <div className="header__mobile-actions">
              <LangSelector />
              {isAuthenticated ? (
                <>
                  <Link to={
                    user?.role === 'vendor' ? '/vendor' :
                    user?.role === 'admin' || user?.role === 'super_admin' ? '/admin' :
                    user?.role === 'venue_owner' ? '/venue' :
                    user?.role === 'coordinator' ? '/coordinator' :
                    '/dashboard'
                  } className="btn btn-primary w-100" onClick={() => setMenuOpen(false)}>
                    {user?.role === 'admin' || user?.role === 'super_admin' ? 'Admin Panel' : t('nav.dashboard')}
                  </Link>
                  <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="btn btn-secondary w-100">{t('nav.logout')}</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn btn-secondary w-100" onClick={() => setMenuOpen(false)}>{t('nav.login')}</Link>
                  <Link to="/register" className="btn btn-primary w-100" onClick={() => setMenuOpen(false)}>{t('nav.register')}</Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
